import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';


const ServiceConfiguratorSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedOptions, setSelectedOptions] = useState({
    companySize: '',
    complexity: '',
    delivery: '',
    timeline: ''
  });
  const [estimatedPrice, setEstimatedPrice] = useState(null);

  const translations = {
    en: {
      title: "Service Package Configurator",
      subtitle: "Get a customized service package with transparent pricing based on your specific requirements",
      companySize: "Company Size",
      complexity: "Project Complexity",
      delivery: "Delivery Method",
      timeline: "Preferred Timeline",
      generateQuote: "Generate Quote",
      estimatedPrice: "Estimated Price Range",
      requestConsultation: "Request Detailed Consultation"
    },
    hi: {
      title: "सेवा पैकेज कॉन्फ़िगरेटर",
      subtitle: "अपनी विशिष्ट आवश्यकताओं के आधार पर पारदर्शी मूल्य निर्धारण के साथ अनुकूलित सेवा पैकेज प्राप्त करें",
      companySize: "कंपनी का आकार",
      complexity: "परियोजना जटिलता",
      delivery: "डिलीवरी पद्धति",
      timeline: "पसंदीदा समयसीमा",
      generateQuote: "कोटेशन जनरेट करें",
      estimatedPrice: "अनुमानित मूल्य सीमा",
      requestConsultation: "विस्तृत परामर्श का अनुरोध"
    }
  };

  const t = translations[currentLanguage];

  const configOptions = {
    companySize: [
      { value: 'small', label: currentLanguage === 'en' ? 'Small (1-50 employees)' : 'छोटी (1-50 कर्मचारी)', multiplier: 1 },
      { value: 'medium', label: currentLanguage === 'en' ? 'Medium (51-200 employees)' : 'मध्यम (51-200 कर्मचारी)', multiplier: 1.5 },
      { value: 'large', label: currentLanguage === 'en' ? 'Large (200+ employees)' : 'बड़ी (200+ कर्मचारी)', multiplier: 2.5 }
    ],
    complexity: [
      { value: 'basic', label: currentLanguage === 'en' ? 'Basic Setup' : 'बुनियादी सेटअप', multiplier: 1 },
      { value: 'standard', label: currentLanguage === 'en' ? 'Standard Implementation' : 'मानक कार्यान्वयन', multiplier: 1.5 },
      { value: 'advanced', label: currentLanguage === 'en' ? 'Advanced Customization' : 'उन्नत अनुकूलन', multiplier: 2.2 }
    ],
    delivery: [
      { value: 'onsite', label: currentLanguage === 'en' ? 'On-site' : 'साइट पर', multiplier: 1.3 },
      { value: 'remote', label: currentLanguage === 'en' ? 'Remote' : 'रिमोट', multiplier: 1 },
      { value: 'hybrid', label: currentLanguage === 'en' ? 'Hybrid' : 'हाइब्रिड', multiplier: 1.15 }
    ],
    timeline: [
      { value: 'standard', label: currentLanguage === 'en' ? 'Standard (16-20 weeks)' : 'मानक (16-20 सप्ताह)', multiplier: 1 },
      { value: 'accelerated', label: currentLanguage === 'en' ? 'Accelerated (12-16 weeks)' : 'त्वरित (12-16 सप्ताह)', multiplier: 1.25 },
      { value: 'extended', label: currentLanguage === 'en' ? 'Extended (20+ weeks)' : 'विस्तृत (20+ सप्ताह)', multiplier: 0.9 }
    ]
  };

  const basePrice = 500000; // Base price in INR

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleOptionChange = (category, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const generateQuote = () => {
    const { companySize, complexity, delivery, timeline } = selectedOptions;
    
    if (!companySize || !complexity || !delivery || !timeline) {
      return;
    }

    const sizeMultiplier = configOptions.companySize.find(opt => opt.value === companySize)?.multiplier || 1;
    const complexityMultiplier = configOptions.complexity.find(opt => opt.value === complexity)?.multiplier || 1;
    const deliveryMultiplier = configOptions.delivery.find(opt => opt.value === delivery)?.multiplier || 1;
    const timelineMultiplier = configOptions.timeline.find(opt => opt.value === timeline)?.multiplier || 1;

    const totalMultiplier = sizeMultiplier * complexityMultiplier * deliveryMultiplier * timelineMultiplier;
    const estimatedCost = basePrice * totalMultiplier;
    
    setEstimatedPrice({
      min: Math.round(estimatedCost * 0.8),
      max: Math.round(estimatedCost * 1.2)
    });
  };

  const isFormComplete = Object.values(selectedOptions).every(value => value !== '');

  return (
    <section id="service-configurator" className="py-16 bg-surface-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Configurator Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-border">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Company Size */}
            <div>
              <label className="block text-sm font-medium text-primary mb-4">
                {t.companySize}
              </label>
              <div className="space-y-3">
                {configOptions.companySize.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="companySize"
                      value={option.value}
                      checked={selectedOptions.companySize === option.value}
                      onChange={(e) => handleOptionChange('companySize', e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-text-secondary">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Complexity */}
            <div>
              <label className="block text-sm font-medium text-primary mb-4">
                {t.complexity}
              </label>
              <div className="space-y-3">
                {configOptions.complexity.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="complexity"
                      value={option.value}
                      checked={selectedOptions.complexity === option.value}
                      onChange={(e) => handleOptionChange('complexity', e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-text-secondary">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Delivery Method */}
            <div>
              <label className="block text-sm font-medium text-primary mb-4">
                {t.delivery}
              </label>
              <div className="space-y-3">
                {configOptions.delivery.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="delivery"
                      value={option.value}
                      checked={selectedOptions.delivery === option.value}
                      onChange={(e) => handleOptionChange('delivery', e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-text-secondary">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <label className="block text-sm font-medium text-primary mb-4">
                {t.timeline}
              </label>
              <div className="space-y-3">
                {configOptions.timeline.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="timeline"
                      value={option.value}
                      checked={selectedOptions.timeline === option.value}
                      onChange={(e) => handleOptionChange('timeline', e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-text-secondary">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Quote Button */}
          <div className="mt-8 text-center">
            <Button
              variant="primary"
              onClick={generateQuote}
              disabled={!isFormComplete}
              className="bg-gradient-accent hover:bg-accent-600 shadow-cta"
              iconName="Calculator"
              iconPosition="left"
              iconSize={20}
            >
              {t.generateQuote}
            </Button>
          </div>

          {/* Price Estimate */}
          {estimatedPrice && (
            <div className="mt-8 p-6 bg-success-50 rounded-xl border border-success-200">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-success mb-2">
                  {t.estimatedPrice}
                </h3>
                <div className="text-3xl font-bold text-success mb-4">
                  ₹{estimatedPrice.min.toLocaleString('en-IN')} - ₹{estimatedPrice.max.toLocaleString('en-IN')}
                </div>
                <p className="text-sm text-text-secondary mb-6">
                  {currentLanguage === 'en' ?'Final pricing may vary based on detailed requirements analysis' :'विस्तृत आवश्यकताओं के विश्लेषण के आधार पर अंतिम मूल्य निर्धारण अलग हो सकता है'}
                </p>
                <Button
                  variant="outline"
                  className="border-success text-success hover:bg-success-50"
                  iconName="Phone"
                  iconPosition="left"
                  iconSize={16}
                >
                  {t.requestConsultation}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceConfiguratorSection;